/**
 * PropertyDataForSEO — the DFS data-layer view for one property.
 *
 * Mirrors PropertyAhrefs's shape but with DFS's broader endpoint mix.
 * Tabs grouped by API surface: Backlinks · Labs (keywords + competitive)
 * · SERP · Pages · Domain · Brand monitoring.
 *
 * Reads cached source_data via /capabilities/{vendor}/sample?property_id=N.
 * Refresh button triggers POST /properties/{id}/dataforseo/pull which
 * fires every wired endpoint in parallel.
 */
import { useState } from "react";
import { useParams } from "react-router-dom";
import { Card } from "@tremor/react";
import {
  RefreshCw, Loader2, ExternalLink, AlertTriangle,
  Database, Link as LinkIcon, Tag, BarChart3, Globe,
  Sparkles,
} from "lucide-react";
import {
  useProperty, useCapabilitySample, useStartDataForSEOPull,
} from "@/lib/queries";
import { cn } from "@/lib/utils";


type Tab =
  | "summary"
  | "backlinks_summary"
  | "backlinks_list"
  | "refdomains"
  | "anchors"
  | "ref_networks"
  | "competitors_link"
  | "ranked_keywords"
  | "labs_competitors"
  | "domain_rank"
  | "relevant_pages"
  | "pages_backlinks"
  | "search_intent"
  | "ai_summary"
  | "tech_stack"
  | "whois"
  | "lighthouse"
  | "on_page";


export default function PropertyDataForSEO() {
  const { propertyId: rawId } = useParams<{ propertyId: string }>();
  const propertyId = Number(rawId);
  const property = useProperty(propertyId);
  const [tab, setTab] = useState<Tab>("summary");
  const pull = useStartDataForSEOPull(propertyId);

  const onRefresh = async () => {
    if (!confirm(
      "Pull every wired DataForSEO endpoint for this property? "
      + "Uses ~$0.10-$0.50 of DFS credits (depending on tracked-keyword "
      + "count). Takes ~30-60s. Cached results served if fresh (<24h)."
    )) return;
    try { await pull.mutateAsync({ force: false }); } catch { /* surfaced inline */ }
  };
  const onForce = async () => {
    if (!confirm(
      "Force fresh pull (bypass cache)? Uses ~$0.10-$0.50 of DFS credits."
    )) return;
    try { await pull.mutateAsync({ force: true }); } catch { /* surfaced inline */ }
  };

  if (property.isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (!property.data) return <div className="text-sm text-red-600">Property not found.</div>;
  const p = property.data;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5 text-primary" />
            <h1 className="text-2xl font-semibold tracking-tight">DataForSEO</h1>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            DFS multi-source view of <span className="font-mono">{p.domain}</span> —
            backlinks, ranked keywords, SERP, on-page, brand monitoring.
            Cross-vendor disagreement signal vs Ahrefs.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button type="button" onClick={onRefresh} disabled={pull.isPending}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
            {pull.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </button>
          <button type="button" onClick={onForce} disabled={pull.isPending}
            title="Force fresh pull, bypass cache"
            className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-2 text-xs hover:bg-accent disabled:opacity-50">
            Force
          </button>
        </div>
      </div>

      {pull.error && (
        <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-700 dark:text-red-400">
          {pull.error instanceof Error ? pull.error.message : "Pull failed"}
        </div>
      )}
      {pull.isSuccess && pull.data && (
        <PullSummary data={pull.data} />
      )}

      {/* Tab strip — grouped by API surface */}
      <div className="space-y-1 border-b border-border">
        <div className="flex flex-wrap items-center gap-1">
          <TabBtn active={tab === "summary"} onClick={() => setTab("summary")}>Overview</TabBtn>
          <span className="px-2 text-xs text-muted-foreground">Backlinks:</span>
          <TabBtn active={tab === "backlinks_summary"}  onClick={() => setTab("backlinks_summary")}>Summary</TabBtn>
          <TabBtn active={tab === "backlinks_list"}     onClick={() => setTab("backlinks_list")}>Backlinks</TabBtn>
          <TabBtn active={tab === "refdomains"}         onClick={() => setTab("refdomains")}>Refdomains</TabBtn>
          <TabBtn active={tab === "anchors"}            onClick={() => setTab("anchors")}>Anchors</TabBtn>
          <TabBtn active={tab === "ref_networks"}       onClick={() => setTab("ref_networks")}>Networks</TabBtn>
          <TabBtn active={tab === "competitors_link"}   onClick={() => setTab("competitors_link")}>Link competitors</TabBtn>
          <TabBtn active={tab === "pages_backlinks"}    onClick={() => setTab("pages_backlinks")}>Pages</TabBtn>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          <span className="px-2 text-xs text-muted-foreground">Search:</span>
          <TabBtn active={tab === "ranked_keywords"}    onClick={() => setTab("ranked_keywords")}>Ranked keywords</TabBtn>
          <TabBtn active={tab === "labs_competitors"}   onClick={() => setTab("labs_competitors")}>Organic competitors</TabBtn>
          <TabBtn active={tab === "domain_rank"}        onClick={() => setTab("domain_rank")}>Visibility</TabBtn>
          <TabBtn active={tab === "relevant_pages"}     onClick={() => setTab("relevant_pages")}>Top pages</TabBtn>
          <TabBtn active={tab === "search_intent"}      onClick={() => setTab("search_intent")}>Intent</TabBtn>
          <TabBtn active={tab === "ai_summary"}         onClick={() => setTab("ai_summary")}>AI Overview</TabBtn>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          <span className="px-2 text-xs text-muted-foreground">Site:</span>
          <TabBtn active={tab === "tech_stack"}         onClick={() => setTab("tech_stack")}>Tech stack</TabBtn>
          <TabBtn active={tab === "whois"}              onClick={() => setTab("whois")}>WHOIS</TabBtn>
          <TabBtn active={tab === "on_page"}            onClick={() => setTab("on_page")}>On-page</TabBtn>
          <TabBtn active={tab === "lighthouse"}         onClick={() => setTab("lighthouse")}>Lighthouse</TabBtn>
        </div>
      </div>

      <div>
        {tab === "summary"           && <OverviewTab propertyId={propertyId} domain={p.domain} />}
        {tab === "backlinks_summary" && <RawJsonTab title="Backlinks summary"        propertyId={propertyId} endpoint="backlinks_summary_live" />}
        {tab === "backlinks_list"    && <BacklinksListTab propertyId={propertyId} domain={p.domain} />}
        {tab === "refdomains"        && <RefdomainsTab propertyId={propertyId} domain={p.domain} />}
        {tab === "anchors"           && <AnchorsTab propertyId={propertyId} domain={p.domain} />}
        {tab === "ref_networks"      && <RefNetworksTab propertyId={propertyId} domain={p.domain} />}
        {tab === "competitors_link"  && <LinkCompetitorsTab propertyId={propertyId} domain={p.domain} />}
        {tab === "pages_backlinks"   && <PagesBacklinksTab propertyId={propertyId} domain={p.domain} />}
        {tab === "ranked_keywords"   && <RankedKeywordsTab propertyId={propertyId} domain={p.domain} />}
        {tab === "labs_competitors"  && <LabsCompetitorsTab propertyId={propertyId} domain={p.domain} />}
        {tab === "domain_rank"       && <DomainRankTab propertyId={propertyId} domain={p.domain} />}
        {tab === "relevant_pages"    && <RelevantPagesTab propertyId={propertyId} domain={p.domain} />}
        {tab === "search_intent"     && <SearchIntentTab propertyId={propertyId} />}
        {tab === "ai_summary"        && <RawJsonTab title="AI Overview (top tracked keyword)" propertyId={propertyId} endpoint="serp_ai_summary" />}
        {tab === "tech_stack"        && <RawJsonTab title="Detected tech stack" propertyId={propertyId} endpoint="domain_analytics_technologies_domain_technologies_live" />}
        {tab === "whois"             && <RawJsonTab title="WHOIS (enriched)" propertyId={propertyId} endpoint="domain_analytics_whois_overview_live" />}
        {tab === "on_page"           && <RawJsonTab title="On-page audit (homepage)" propertyId={propertyId} endpoint="on_page_instant" />}
        {tab === "lighthouse"        && <RawJsonTab title="Lighthouse (DFS-side, homepage)" propertyId={propertyId} endpoint="on_page_lighthouse_live" />}
      </div>
    </div>
  );
}


function PullSummary({ data }: { data: { duration_s: number; results: Array<{ ok: boolean; endpoint: string; rows: number | null; error: string | null }> } }) {
  const ok = data.results.filter((r) => r.ok).length;
  const failed = data.results.filter((r) => !r.ok);
  return (
    <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs">
      <div className="font-medium text-emerald-700 dark:text-emerald-400">
        Pulled {ok}/{data.results.length} endpoints in {data.duration_s.toFixed(1)}s.
      </div>
      {failed.length > 0 && (
        <details className="mt-1">
          <summary className="cursor-pointer text-amber-700 dark:text-amber-400">{failed.length} failed</summary>
          <ul className="mt-1 space-y-0.5 pl-3">
            {failed.map((r) => (
              <li key={r.endpoint} className="font-mono text-[10px]">
                <span className="text-muted-foreground">{r.endpoint}:</span>{" "}
                <span className="text-red-700 dark:text-red-400">{r.error}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}


function TabBtn({ active, onClick, children }: {
  active: boolean; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <button type="button" onClick={onClick}
      className={cn(
        "border-b-2 px-2.5 py-1.5 text-[12px] font-medium transition-colors",
        active ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground",
      )}>
      {children}
    </button>
  );
}


// ─── Tab components ────────────────────────────────────────────────


function OverviewTab({ propertyId, domain: _domain }: { propertyId: number; domain: string }) {
  const summary = useCapabilitySample("dataforseo", "backlinks_summary_live",   { propertyId });
  const ranked  = useCapabilitySample("dataforseo", "labs_ranked_keywords_live", { propertyId });
  const rank    = useCapabilitySample("dataforseo", "labs_domain_rank_overview_live", { propertyId });

  const sumR = (summary.data?.response as Record<string, unknown> | undefined)?.result as Record<string, unknown> | undefined;
  const rankedItems = (ranked.data?.response as Record<string, unknown> | undefined)?.items as unknown[] | undefined;
  const rankR = (rank.data?.response as Record<string, unknown> | undefined)?.result as Record<string, unknown> | undefined;

  const refDomains = sumR?.referring_domains as number | undefined;
  const backlinks = sumR?.backlinks as number | undefined;
  const spamScore = sumR?.backlinks_spam_score as number | undefined;
  const totalRanked = rankedItems?.length;
  const orgEtv = ((rankR?.metrics as Record<string, unknown> | undefined)?.organic as Record<string, unknown> | undefined)?.etv as number | undefined;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Headline label="Refdomains (DFS)"      value={refDomains} />
        <Headline label="Total backlinks"       value={backlinks} />
        <Headline label="Spam score"            value={spamScore != null ? `${spamScore}/100` : undefined} />
        <Headline label="Ranked keywords"       value={totalRanked} />
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Headline label="Organic ETV (DFS)"     value={orgEtv != null ? `~${Math.round(orgEtv)}` : undefined} sub="visits/mo estimate" />
      </div>
      <Card>
        <div className="text-sm">
          The DFS lens is the cross-validation layer for everything Ahrefs
          reports. Headline counts above are from DFS's own index — compare
          with Ahrefs to spot index disagreement (already wired as the{" "}
          <code className="rounded bg-muted px-1 text-[11px]">refdomains_index_divergence</code>
          {" "}finding when ratio &gt; 2x).
        </div>
        <div className="mt-2 text-xs text-muted-foreground">
          Click the tabs above to drill into each data category. If a tab
          shows "no data yet", click <strong>Refresh</strong> to pull every
          wired endpoint at once.
        </div>
      </Card>
    </div>
  );
}


function BacklinksListTab({ propertyId, domain }: { propertyId: number; domain: string }) {
  const sample = useCapabilitySample("dataforseo", "backlinks_list_live", { propertyId });
  const rows = (sample.data?.response as Record<string, unknown> | undefined)?.items as Array<Record<string, unknown>> | undefined ?? [];
  return (
    <DataTable
      title={`Backlinks (DFS index) for ${domain}`}
      subtitle="Top 100 backlinks by DFS rank. Cross-validate with Ahrefs all-backlinks (different index, may catch links Ahrefs misses)."
      sample={sample.data} rowCount={rows.length}
      icon={<LinkIcon className="h-5 w-5 text-primary" />}
      columns={[
        { key: "url_from", label: "Source page", className: "font-mono text-[11px] max-w-[26%]",
          render: (r) => <ExtLink href={String(r.url_from ?? "")} /> },
        { key: "url_to", label: "Target", className: "font-mono text-[11px] max-w-[24%]",
          render: (r) => <ExtLink href={String(r.url_to ?? "")} /> },
        { key: "anchor", label: "Anchor", className: "max-w-[18%] truncate" },
        { key: "rank", label: "Rank", align: "right" },
        { key: "domain_from_rank", label: "Source rank", align: "right" },
        { key: "dofollow", label: "Type", align: "right",
          render: (r) => r.dofollow ? "dofollow" : <span className="text-muted-foreground">nofollow</span> },
      ]}
      rows={rows}
    />
  );
}


function RefdomainsTab({ propertyId, domain }: { propertyId: number; domain: string }) {
  const sample = useCapabilitySample("dataforseo", "referring_domains_live", { propertyId });
  const rows = (sample.data?.response as Record<string, unknown> | undefined)?.items as Array<Record<string, unknown>> | undefined ?? [];
  return (
    <DataTable
      title={`Referring domains (DFS) for ${domain}`}
      subtitle="Top 100 by DFS rank. Three-way truth when combined with Ahrefs refdomains and GSC link report."
      sample={sample.data} rowCount={rows.length}
      icon={<LinkIcon className="h-5 w-5 text-primary" />}
      columns={[
        { key: "domain", label: "Domain", className: "font-mono text-[11px]" },
        { key: "rank", label: "Rank", align: "right" },
        { key: "backlinks", label: "Backlinks", align: "right",
          render: (r) => Number(r.backlinks ?? 0).toLocaleString() },
        { key: "first_seen", label: "First seen", align: "right",
          render: (r) => formatDateShort(String(r.first_seen ?? "")) },
        { key: "lost_date", label: "Lost", align: "right",
          render: (r) => r.lost_date ? formatDateShort(String(r.lost_date)) : "—" },
      ]}
      rows={rows}
    />
  );
}


function AnchorsTab({ propertyId, domain }: { propertyId: number; domain: string }) {
  const sample = useCapabilitySample("dataforseo", "backlink_anchors_live", { propertyId });
  const rows = (sample.data?.response as Record<string, unknown> | undefined)?.items as Array<Record<string, unknown>> | undefined ?? [];
  return (
    <DataTable
      title={`Anchor distribution (DFS) for ${domain}`}
      subtitle="Top 100 anchors by backlink count."
      sample={sample.data} rowCount={rows.length}
      icon={<Tag className="h-5 w-5 text-primary" />}
      columns={[
        { key: "anchor", label: "Anchor text", className: "max-w-[40%] truncate" },
        { key: "backlinks", label: "Backlinks", align: "right",
          render: (r) => Number(r.backlinks ?? 0).toLocaleString() },
        { key: "referring_domains", label: "Ref domains", align: "right" },
        { key: "rank", label: "Rank", align: "right" },
        { key: "first_seen", label: "First seen", align: "right",
          render: (r) => formatDateShort(String(r.first_seen ?? "")) },
      ]}
      rows={rows}
    />
  );
}


function RefNetworksTab({ propertyId, domain }: { propertyId: number; domain: string }) {
  const sample = useCapabilitySample("dataforseo", "backlinks_referring_networks_live", { propertyId });
  const rows = (sample.data?.response as Record<string, unknown> | undefined)?.items as Array<Record<string, unknown>> | undefined ?? [];
  return (
    <DataTable
      title={`Referring networks (IP / subnet) for ${domain}`}
      subtitle="PBN cluster detection — many domains, same IP/subnet = high spam risk."
      sample={sample.data} rowCount={rows.length}
      icon={<AlertTriangle className="h-5 w-5 text-amber-700 dark:text-amber-400" />}
      columns={[
        { key: "ip", label: "IP / Network", className: "font-mono text-[11px]" },
        { key: "country", label: "Country" },
        { key: "referring_domains", label: "Ref domains", align: "right" },
        { key: "backlinks", label: "Backlinks", align: "right",
          render: (r) => Number(r.backlinks ?? 0).toLocaleString() },
        { key: "first_seen", label: "First seen", align: "right",
          render: (r) => formatDateShort(String(r.first_seen ?? "")) },
      ]}
      rows={rows}
    />
  );
}


function LinkCompetitorsTab({ propertyId, domain }: { propertyId: number; domain: string }) {
  const sample = useCapabilitySample("dataforseo", "backlinks_competitors_live", { propertyId });
  const rows = (sample.data?.response as Record<string, unknown> | undefined)?.items as Array<Record<string, unknown>> | undefined ?? [];
  return (
    <DataTable
      title={`Link-overlap competitors of ${domain}`}
      subtitle="Domains with the most shared backlinks — different signal than organic-keyword competitors. These are sites in the same content niche based on who links to them."
      sample={sample.data} rowCount={rows.length}
      icon={<LinkIcon className="h-5 w-5 text-primary" />}
      columns={[
        { key: "target", label: "Domain", className: "font-mono text-[11px]" },
        { key: "rank", label: "Rank", align: "right" },
        { key: "backlinks", label: "Backlinks", align: "right",
          render: (r) => Number(r.backlinks ?? 0).toLocaleString() },
        { key: "referring_domains", label: "Ref domains", align: "right" },
        { key: "intersections", label: "Shared backlinks", align: "right" },
      ]}
      rows={rows}
    />
  );
}


function PagesBacklinksTab({ propertyId, domain }: { propertyId: number; domain: string }) {
  const sample = useCapabilitySample("dataforseo", "backlinks_domain_pages_summary_live", { propertyId });
  const rows = (sample.data?.response as Record<string, unknown> | undefined)?.items as Array<Record<string, unknown>> | undefined ?? [];
  return (
    <DataTable
      title={`Pages by backlinks for ${domain}`}
      subtitle="Per-page backlink summary — which URLs on this site attract the most inbound links."
      sample={sample.data} rowCount={rows.length}
      icon={<Globe className="h-5 w-5 text-primary" />}
      columns={[
        { key: "url", label: "URL", className: "font-mono text-[11px] max-w-[36%]",
          render: (r) => <ExtLink href={String(r.url ?? "")} /> },
        { key: "backlinks", label: "Backlinks", align: "right",
          render: (r) => Number(r.backlinks ?? 0).toLocaleString() },
        { key: "referring_domains", label: "Ref domains", align: "right" },
        { key: "rank", label: "Rank", align: "right" },
        { key: "first_seen", label: "First seen", align: "right",
          render: (r) => formatDateShort(String(r.first_seen ?? "")) },
      ]}
      rows={rows}
    />
  );
}


function RankedKeywordsTab({ propertyId, domain }: { propertyId: number; domain: string }) {
  const sample = useCapabilitySample("dataforseo", "labs_ranked_keywords_live", { propertyId });
  const rows = (sample.data?.response as Record<string, unknown> | undefined)?.items as Array<Record<string, unknown>> | undefined ?? [];
  return (
    <DataTable
      title={`Ranked keywords (DFS index) for ${domain}`}
      subtitle="Every keyword DFS sees this domain ranking for. Top 1000 by volume. Cross-validates Ahrefs's organic-keywords (different index)."
      sample={sample.data} rowCount={rows.length}
      icon={<Tag className="h-5 w-5 text-primary" />}
      columns={[
        { key: "keyword", label: "Keyword", className: "max-w-[26%] truncate",
          render: (r) => {
            const kd = (r.keyword_data as Record<string, unknown> | undefined);
            return String((kd?.keyword) ?? r.keyword ?? "");
          }},
        { key: "position", label: "Pos", align: "right",
          render: (r) => {
            const sr = (r.ranked_serp_element as Record<string, unknown> | undefined);
            const se = (sr?.serp_item as Record<string, unknown> | undefined);
            return String(se?.rank_absolute ?? "—");
          }},
        { key: "search_volume", label: "Volume", align: "right",
          render: (r) => {
            const kd = (r.keyword_data as Record<string, unknown> | undefined);
            const ki = (kd?.keyword_info as Record<string, unknown> | undefined);
            const v = ki?.search_volume as number | undefined;
            return v != null ? v.toLocaleString() : "—";
          }},
        { key: "cpc", label: "CPC", align: "right",
          render: (r) => {
            const kd = (r.keyword_data as Record<string, unknown> | undefined);
            const ki = (kd?.keyword_info as Record<string, unknown> | undefined);
            const c = ki?.cpc as number | undefined;
            return c != null ? `$${c.toFixed(2)}` : "—";
          }},
        { key: "url", label: "Landing URL", className: "font-mono text-[11px] max-w-[28%]",
          render: (r) => {
            const sr = (r.ranked_serp_element as Record<string, unknown> | undefined);
            const se = (sr?.serp_item as Record<string, unknown> | undefined);
            const u = se?.relative_url as string | undefined ?? se?.url as string | undefined;
            return u ? <ExtLink href={String(u)} /> : "—";
          }},
      ]}
      rows={rows}
    />
  );
}


function LabsCompetitorsTab({ propertyId, domain }: { propertyId: number; domain: string }) {
  const sample = useCapabilitySample("dataforseo", "competitors_domain_live", { propertyId });
  const rows = (sample.data?.response as Record<string, unknown> | undefined)?.items as Array<Record<string, unknown>> | undefined ?? [];
  return (
    <DataTable
      title={`Organic competitors (DFS) of ${domain}`}
      subtitle="Top 30 by keyword overlap. Cross-validates Ahrefs's competitor list."
      sample={sample.data} rowCount={rows.length}
      icon={<BarChart3 className="h-5 w-5 text-primary" />}
      columns={[
        { key: "domain", label: "Competitor", className: "font-mono text-[11px]" },
        { key: "intersections", label: "Common kw", align: "right",
          render: (r) => Number(r.intersections ?? 0).toLocaleString() },
        { key: "full_domain_metrics", label: "Their kw count", align: "right",
          render: (r) => {
            const m = (r.full_domain_metrics as Record<string, unknown> | undefined);
            const o = m?.organic as Record<string, unknown> | undefined;
            return o?.count != null ? Number(o.count).toLocaleString() : "—";
          }},
        { key: "etv", label: "Their ETV", align: "right",
          render: (r) => {
            const m = (r.full_domain_metrics as Record<string, unknown> | undefined);
            const o = m?.organic as Record<string, unknown> | undefined;
            return o?.etv != null ? Math.round(Number(o.etv)).toLocaleString() : "—";
          }},
      ]}
      rows={rows}
    />
  );
}


function DomainRankTab({ propertyId, domain }: { propertyId: number; domain: string }) {
  const sample = useCapabilitySample("dataforseo", "labs_domain_rank_overview_live", { propertyId });
  return <RawJsonTabRender title={`Domain rank overview for ${domain}`} sample={sample.data} />;
}


function RelevantPagesTab({ propertyId, domain }: { propertyId: number; domain: string }) {
  const sample = useCapabilitySample("dataforseo", "labs_relevant_pages_live", { propertyId });
  const rows = (sample.data?.response as Record<string, unknown> | undefined)?.items as Array<Record<string, unknown>> | undefined ?? [];
  return (
    <DataTable
      title={`Top pages by traffic (DFS) for ${domain}`}
      subtitle="Per-page rank/traffic/ETV from DFS's index."
      sample={sample.data} rowCount={rows.length}
      icon={<Globe className="h-5 w-5 text-primary" />}
      columns={[
        { key: "page_address", label: "URL", className: "font-mono text-[11px] max-w-[40%]",
          render: (r) => <ExtLink href={String(r.page_address ?? "")} /> },
        { key: "metrics_organic_count", label: "Keywords", align: "right",
          render: (r) => {
            const m = (r.metrics as Record<string, unknown> | undefined);
            const o = m?.organic as Record<string, unknown> | undefined;
            return o?.count != null ? Number(o.count).toLocaleString() : "—";
          }},
        { key: "metrics_organic_etv", label: "ETV", align: "right",
          render: (r) => {
            const m = (r.metrics as Record<string, unknown> | undefined);
            const o = m?.organic as Record<string, unknown> | undefined;
            return o?.etv != null ? Math.round(Number(o.etv)).toLocaleString() : "—";
          }},
        { key: "page_metrics_ahrefs_rank", label: "Rank", align: "right" },
      ]}
      rows={rows}
    />
  );
}


function SearchIntentTab({ propertyId }: { propertyId: number }) {
  const sample = useCapabilitySample("dataforseo", "labs_search_intent_live", { propertyId });
  const rows = (sample.data?.response as Record<string, unknown> | undefined)?.items as Array<Record<string, unknown>> | undefined ?? [];
  return (
    <DataTable
      title="Search intent for tracked keywords"
      subtitle="DFS classifies each keyword as informational / navigational / commercial / transactional. Used to detect intent-mismatched landing pages."
      sample={sample.data} rowCount={rows.length}
      icon={<Sparkles className="h-5 w-5 text-primary" />}
      columns={[
        { key: "keyword", label: "Keyword", className: "max-w-[40%] truncate" },
        { key: "main_intent", label: "Primary intent",
          render: (r) => {
            const ki = (r.keyword_intent as Record<string, unknown> | undefined);
            return String(ki?.label ?? r.main_intent ?? "—");
          }},
        { key: "probability", label: "Confidence", align: "right",
          render: (r) => {
            const ki = (r.keyword_intent as Record<string, unknown> | undefined);
            const p = ki?.probability as number | undefined;
            return p != null ? `${(p * 100).toFixed(0)}%` : "—";
          }},
        { key: "secondary_intents", label: "Secondary",
          render: (r) => {
            const si = r.secondary_keyword_intents as Array<Record<string, unknown>> | undefined;
            if (!si || si.length === 0) return "—";
            return si.map((x) => x.label as string).join(", ");
          }},
      ]}
      rows={rows}
    />
  );
}


// ─── Generic JSON viewer (for endpoints we don't render as tables yet) ──


function RawJsonTab({ title, propertyId, endpoint }: {
  title: string; propertyId: number; endpoint: string;
}) {
  const sample = useCapabilitySample("dataforseo", endpoint, { propertyId });
  return <RawJsonTabRender title={title} sample={sample.data} />;
}


function RawJsonTabRender({ title, sample }: {
  title: string;
  sample: { available: boolean; fetched_at: string | null; response: unknown; response_size_bytes: number | null } | undefined;
}) {
  return (
    <Card>
      <div className="mb-2 flex items-start justify-between">
        <h2 className="text-sm font-semibold">{title}</h2>
        {sample?.available && (
          <div className="text-[11px] text-muted-foreground">
            Cached {sample.fetched_at && formatDateAgo(sample.fetched_at)} · {sample.response_size_bytes && formatBytes(sample.response_size_bytes)}
          </div>
        )}
      </div>
      {!sample?.available ? (
        <div className="rounded-md border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
          No data yet. Click <strong>Refresh</strong> at the top.
        </div>
      ) : (
        <pre className="max-h-[600px] overflow-auto rounded bg-muted/40 p-2 font-mono text-[10px] leading-relaxed">{JSON.stringify(sample.response, null, 2)}</pre>
      )}
    </Card>
  );
}


// ─── Generic data-table component (mirrors PropertyAhrefs's) ────────


type Column = {
  key: string;
  label: string;
  align?: "right";
  className?: string;
  render?: (row: Record<string, unknown>) => React.ReactNode;
};


function DataTable({
  title, subtitle, columns, rows, sample, rowCount, icon,
}: {
  title: string;
  subtitle: string;
  columns: Column[];
  rows: Array<Record<string, unknown>>;
  sample: { fetched_at: string | null; available: boolean } | undefined;
  rowCount: number;
  icon?: React.ReactNode;
}) {
  return (
    <Card>
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            {icon}
            <h2 className="text-sm font-semibold">{title}</h2>
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>
        </div>
        {sample?.available && sample.fetched_at && (
          <div className="shrink-0 text-right text-[11px] text-muted-foreground">
            <div>Cached {formatDateAgo(sample.fetched_at)}</div>
            <div>{rowCount.toLocaleString()} rows</div>
          </div>
        )}
      </div>

      {!sample?.available ? (
        <div className="rounded-md border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
          No data yet for this endpoint. Click <strong>Refresh</strong> at the top to pull.
        </div>
      ) : rowCount === 0 ? (
        <div className="rounded-md border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
          The endpoint returned 0 rows.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="border-b border-border text-left">
              <tr>
                {columns.map((c) => (
                  <th key={c.key} className={cn(
                    "px-2 py-1.5 font-medium text-muted-foreground",
                    c.align === "right" && "text-right",
                  )}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r, i) => (
                <tr key={i} className="hover:bg-accent/40">
                  {columns.map((c) => (
                    <td key={c.key} className={cn(
                      "px-2 py-1.5 align-top",
                      c.align === "right" && "text-right",
                      c.className,
                    )}>
                      {c.render ? c.render(r) : (r[c.key] != null ? String(r[c.key]) : "—")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}


function Headline({ label, value, sub }: {
  label: string;
  value: number | string | undefined;
  sub?: string;
}) {
  const display = value === undefined || value === null
    ? "no data"
    : (typeof value === "number" ? value.toLocaleString() : value);
  const isNoData = value === undefined || value === null;
  return (
    <Card>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={cn("mt-0.5 text-2xl font-semibold", isNoData && "text-muted-foreground italic")}>
        {display}
      </div>
      {sub && <div className="mt-0.5 text-[11px] text-muted-foreground">{sub}</div>}
    </Card>
  );
}


// ─── helpers ────────────────────────────────────────────────────────


function ExtLink({ href }: { href: string }) {
  return (
    <a href={href} target="_blank" rel="noreferrer"
       className="inline-flex items-center gap-0.5 hover:underline" title={href}>
      <span className="truncate">{stripScheme(href)}</span>
      <ExternalLink className="h-3 w-3 opacity-50" />
    </a>
  );
}

function stripScheme(url: string): string {
  return url.replace(/^https?:\/\//, "").replace(/^www\./, "");
}

function formatDateShort(iso: string): string {
  if (!iso) return "—";
  return iso.slice(0, 10);
}

function formatDateAgo(iso: string): string {
  const t = new Date(iso).getTime();
  const sec = Math.round((Date.now() - t) / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.round(sec / 3600)}h ago`;
  return `${Math.round(sec / 86400)}d ago`;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)}KB`;
  return `${(n / 1024 / 1024).toFixed(1)}MB`;
}
